// scheduling/pages/SuggestionsTab.jsx
// Dev triage surface for Feedback.Suggestions (ContextApp='rprs'). Rendered as
// the AdminConsole "Suggestions" tab, gated on isSuperUser || isDevSuperUser. Lists suggestions
// (scope='all'), expands a row for full detail, and advances one or many
// (per-row or bulk) — each transition writes the workflow columns via
// func-shared-services and notifies the submitter in-app. Sibling of BugReportsTab,
// minus the captured-diagnostics (suggestions don't carry logs/events).
//
// Data: services/suggestions — fetchSuggestions / resolveSuggestions / notifySuggestionUpdated.

import { useState, useEffect, useCallback } from 'react';
import { fetchSuggestions, resolveSuggestions, notifySuggestionUpdated } from '../services/suggestions';
import { BRAND } from '../utils/schedConstants';

const STATUS_META = {
  under_review: { label: 'Under review', bg: '#FEF3C7', fg: '#92400E' },
  planned:      { label: 'Planned',      bg: '#DBEAFE', fg: '#1D4ED8' },
  shipped:      { label: 'Shipped',      bg: '#D1FAE5', fg: '#065F46' },
  declined:     { label: 'Declined',     bg: '#F3F4F6', fg: '#6B7280' },
};
const STATUS_KEYS = ['under_review', 'planned', 'shipped', 'declined'];
// Statuses that notify the submitter when reached (every dev transition).
const NOTIFY_STATUSES = new Set(['planned', 'shipped', 'declined']);

const CATEGORY_LABELS = {
  new_feature: 'New Feature',
  improvement: 'Improvement',
  ui:          'UI polish',
  other:       'Other',
};

function fmtAge(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.under_review;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      background: m.bg, color: m.fg, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

export default function SuggestionsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('under_review');
  const [search, setSearch] = useState('');

  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);       // full suggestion for expandedId
  const [detailLoading, setDetailLoading] = useState(false);

  // Per-row resolve form (single expanded row at a time).
  const [form, setForm] = useState({ status: 'planned', note: '', ref: '' });
  const [actioning, setActioning] = useState(false);

  // Bulk selection.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState('planned');
  const [bulkNote, setBulkNote] = useState('');

  const load = useCallback(() => {
    setLoading(true); setError(null);
    fetchSuggestions({ scope: 'all', status: statusFilter === 'all' ? undefined : statusFilter })
      .then(res => setList(res.suggestions || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Collapse + clear selection whenever the filter changes (ids may leave the list).
  useEffect(() => { setExpandedId(null); setSelected(new Set()); }, [statusFilter]);

  const openRow = (r) => {
    if (expandedId === r.suggestionId) { setExpandedId(null); return; }
    setExpandedId(r.suggestionId);
    // Default the dropdown to the approve path (Planned) for a fresh suggestion.
    setForm({ status: r.status === 'under_review' ? 'planned' : r.status, note: r.resolutionNote || '', ref: r.addressedInRef || '' });
    setDetail(null);
    setDetailLoading(true);
    fetchSuggestions({ scope: 'all', id: r.suggestionId })
      .then(res => setDetail(res.suggestion || null))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  // Advance a set of ids to a status, then notify the affected submitters.
  const doResolve = async (ids, status, resolutionNote, addressedInRef) => {
    const res = await resolveSuggestions({ ids, status, resolutionNote, addressedInRef });
    const resolved = res.resolved || [];
    const toNotify = resolved.filter(s => NOTIFY_STATUSES.has(s.status));
    if (toNotify.length) {
      // Soft-fail: the transition already landed; a notify hiccup shouldn't error the action.
      try { await notifySuggestionUpdated(toNotify); } catch (_) { /* ignore */ }
    }
    return resolved.length;
  };

  const handleRowResolve = async () => {
    if (!expandedId) return;
    setActioning(true);
    try {
      await doResolve([expandedId], form.status, form.note, form.ref);
      setExpandedId(null);
      load();
    } catch (e) {
      alert('Update failed: ' + e.message);
    } finally {
      setActioning(false);
    }
  };

  const handleBulkResolve = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const m = STATUS_META[bulkStatus];
    if (!window.confirm(`Set ${ids.length} suggestion${ids.length > 1 ? 's' : ''} to "${m.label}"${NOTIFY_STATUSES.has(bulkStatus) ? ' and notify the submitter(s)' : ''}?`)) return;
    setActioning(true);
    try {
      await doResolve(ids, bulkStatus, bulkNote || undefined);
      setSelected(new Set());
      setBulkNote('');
      load();
    } catch (e) {
      alert('Bulk update failed: ' + e.message);
    } finally {
      setActioning(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Client-side search over title / submitter / route.
  const q = search.trim().toLowerCase();
  const rows = q
    ? list.filter(r =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.submittedBy || '').toLowerCase().includes(q) ||
        (r.route || '').toLowerCase().includes(q))
    : list;

  const TH = { padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 };

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: BRAND.deepTeal }}>Suggestions</h3>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, lineHeight: 1.5 }}>
            User-submitted ideas &amp; feedback for this app. Expand a row to read it, then set a status.
            Marking <strong>Planned</strong>, <strong>Shipped</strong>, or <strong>Declined</strong> notifies the submitter in-app.
          </div>
        </div>
        <button onClick={load} disabled={loading}
          style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer',
            padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {/* Filter + search */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12, fontWeight: 600, color: '#374151' }}>
          <option value="all">All statuses</option>
          {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title / submitter / route…"
          style={{ flex: 1, minWidth: 200, padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12 }} />
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>{rows.length} shown</span>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', marginBottom: 12,
          background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#1D4ED8', fontSize: 12 }}>{selected.size} selected</span>
          <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #BFDBFE', fontSize: 12, fontWeight: 600 }}>
            {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <input value={bulkNote} onChange={e => setBulkNote(e.target.value)} placeholder="Shared note for the submitter(s) (optional)…"
            style={{ flex: 1, minWidth: 200, padding: '5px 8px', borderRadius: 6, border: '1px solid #BFDBFE', fontSize: 12 }} />
          <button onClick={handleBulkResolve} disabled={actioning}
            style={{ background: BRAND.deepTeal, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
              padding: '6px 14px', fontSize: 12, fontWeight: 700 }}>
            {actioning ? 'Applying…' : `Apply to ${selected.size}`}
          </button>
          <button onClick={() => setSelected(new Set())} disabled={actioning}
            style={{ background: 'transparent', color: '#6B7280', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Clear
          </button>
        </div>
      )}

      {/* List */}
      {error ? (
        <div style={{ padding: 16, color: '#DC2626', fontSize: 13 }}>Failed to load: {error}</div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>{'💡'}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#9CA3AF' }}>No suggestions{statusFilter !== 'all' ? ` in "${STATUS_META[statusFilter].label}"` : ''}</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                <th style={{ ...TH, width: 34, textAlign: 'center' }}></th>
                <th style={TH}>Title</th>
                <th style={TH}>Category</th>
                <th style={TH}>Submitted by</th>
                <th style={TH}>Status</th>
                <th style={TH}>When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isExpanded = expandedId === r.suggestionId;
                return (
                  <Row key={r.suggestionId}
                    r={r} isExpanded={isExpanded}
                    checked={selected.has(r.suggestionId)}
                    onCheck={() => toggleSelect(r.suggestionId)}
                    onOpen={() => openRow(r)}
                    detail={isExpanded ? detail : null}
                    detailLoading={isExpanded && detailLoading}
                    form={form} setForm={setForm}
                    actioning={actioning}
                    onResolve={handleRowResolve}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Single row + its expanded detail/resolve panel ──────────────────────────
function Row({ r, isExpanded, checked, onCheck, onOpen, detail, detailLoading, form, setForm, actioning, onResolve }) {
  const d = detail || r;
  return (
    <>
      <tr style={{ borderTop: '1px solid #F3F4F6', background: isExpanded ? '#F9FAFB' : '#fff' }}>
        <td style={{ padding: '10px 0', textAlign: 'center' }}>
          <input type="checkbox" checked={checked} onChange={onCheck} onClick={e => e.stopPropagation()} />
        </td>
        <td style={{ padding: '10px 12px', cursor: 'pointer' }} onClick={onOpen}>
          <div style={{ fontWeight: 600, color: '#111827' }}>
            <span style={{ color: '#9CA3AF', fontWeight: 500, marginRight: 6 }}>{isExpanded ? '▾' : '▸'}</span>
            {r.title || '(no title)'}
          </div>
          {r.route && <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1, marginLeft: 18 }}>{r.route}</div>}
        </td>
        <td style={{ padding: '10px 12px', color: '#374151' }}>{CATEGORY_LABELS[r.category] || 'Other'}</td>
        <td style={{ padding: '10px 12px', color: '#374151' }}>{r.submittedBy || '—'}</td>
        <td style={{ padding: '10px 12px' }}><StatusPill status={r.status} /></td>
        <td style={{ padding: '10px 12px', color: '#6B7280', whiteSpace: 'nowrap' }}>{fmtAge(r.createdAt)}</td>
      </tr>
      {isExpanded && (
        <tr style={{ background: '#F9FAFB' }}>
          <td colSpan={6} style={{ padding: '0 12px 16px 46px' }}>
            {detailLoading ? (
              <div style={{ padding: 16, color: '#9CA3AF', fontSize: 12 }}>Loading detail…</div>
            ) : (
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16 }}>
                {/* Suggestion body */}
                <Field label="Category" value={CATEGORY_LABELS[d.category] || 'Other'} />
                <Field label="Description" value={d.description} />
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, lineHeight: 1.6 }}>
                  {d.url && <div><strong>Raised on:</strong> {d.url}</div>}
                  {r.reviewedBy && <div><strong>Last reviewed by:</strong> {r.reviewedBy} {r.reviewedAt ? `(${fmtAge(r.reviewedAt)})` : ''}</div>}
                  {r.addressedInRef && <div><strong>Addressed in:</strong> {r.addressedInRef}</div>}
                </div>

                {/* Resolve form */}
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #F3F4F6' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280' }}>Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12, fontWeight: 600 }}>
                      {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                    </select>
                    <input value={form.ref} onChange={e => setForm(f => ({ ...f, ref: e.target.value }))}
                      placeholder="Addressed in (commit / PR / branch)"
                      style={{ flex: 1, minWidth: 180, padding: '5px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12 }} />
                  </div>
                  <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                    placeholder={NOTIFY_STATUSES.has(form.status) ? 'Note — shown to the submitter in their notification' : 'Internal note (only shown to the submitter when planned / shipped / declined)'}
                    rows={2}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button onClick={onResolve} disabled={actioning}
                      style={{ background: BRAND.deepTeal, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
                        padding: '7px 16px', fontSize: 12, fontWeight: 700 }}>
                      {actioning ? 'Saving…' : NOTIFY_STATUSES.has(form.status) ? 'Save & notify submitter' : 'Save status'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', marginTop: 2 }}>{value}</div>
    </div>
  );
}
